import moment from 'moment'
import { getConstant } from 'services/constantServices'
import {
  CONSTANTS_OPTIONS,
  GOALS_URL,
  GOAL_STEP_URL,
  USER_CONNECTION_RELATIONSHIP,
  USER_ROLE_TYPES,
  VIRTUES_URL,
  VIRTUE_TYPES,
  GOALS_PROGRESS_URL,
  LEADERSHIP_MODELS_URL,
  COHORT_GOAL_FACILITATION_URL
} from 'services/constants'
import { getRequest } from 'utils/api'
import { ISO_DATETIME_FORMAT } from 'utils/constants'
import { parseGoals, compileGoalSteps } from 'services/goalServices'

export const getGoals = async refresh => {
  let goals = sessionStorage.getItem('goals')
  let leadershipModels = sessionStorage.getItem('leadershipModels')
  const goalsSetAt = sessionStorage.getItem('goalsSetAt')
  if (
    refresh ||
    !goals ||
    !leadershipModels ||
    !goalsSetAt ||
    moment().diff(goalsSetAt, 'hours') > 2
  ) {
    const goalRes = await getRequest(GOALS_URL)
    const leadershipRes = await getRequest(LEADERSHIP_MODELS_URL)
    if (goalRes.status === 200 && leadershipRes.status === 200) {
      sessionStorage.setItem(
        'leadershipModels',
        JSON.stringify(leadershipRes.data.data)
      )
      sessionStorage.setItem('goals', JSON.stringify(goalRes.data.data))
      sessionStorage.setItem('goalsSetAt', moment().format(ISO_DATETIME_FORMAT))
      goals = goalRes.data.data
      leadershipModels = leadershipRes.data.data
    }
  } else {
    goals = JSON.parse(goals)
    leadershipModels = JSON.parse(leadershipModels)
  }
  const parsedGoals = parseGoals(goals, leadershipModels)
  return parsedGoals
}
export const getUserCurrentGoal = async () => {
  const goals = await getGoals()
  return goals.find(g => g.currentGoal)
}
export const getGoalInfoById = async goalId => {
  const goals = await getGoals()
  return goals.find(g => g.id === goalId)
}

export const getGoalName = goal => {
  if (goal) {
    return `${goal.leadershipModelName}: ${goal.name}`
  }
  return null
}

export const getGoalNameById = async goalId => {
  const goal = await getGoalInfoById(goalId)
  if (goal) {
    return getGoalName(goal)
  }
  return null
}

export const getGoalSteps = async goalId => {
  const key = `goal-steps-${goalId}`
  let goalSteps = sessionStorage.getItem(key)
  if (!goalSteps) {
    const res = await getRequest(GOAL_STEP_URL, { goalId })
    if (res.status === 200) {
      const { data } = res.data
      sessionStorage.setItem(key, JSON.stringify(data))
      goalSteps = data
    }
  } else {
    goalSteps = JSON.parse(goalSteps)
  }
  return goalSteps
}

export const getGoalsProgress = async () => {
  let goalsProgress = []
  const res = await getRequest(GOALS_PROGRESS_URL)
  if (res.status === 200) {
    const { data } = res.data
    goalsProgress = data
  }
  return goalsProgress
}

export const getGoalsProgressById = async goalId => {
  const res = await getGoalsProgress()
  return res.find(p => p.goalId === goalId)
}

export const getVirtuesFromGoals = async goals => {
  const goalsRes = goals ? goals : await getGoals(true)
  let availableGoals = goalsRes.filter(goal => !goal.isLocked)
  const virtues = [
    ...new Map(
      availableGoals.flatMap(goal => goal.virtues).map(v => [v.name, v])
    ).values()
  ].sort((a, b) => a.name?.localeCompare(b.name))
  return virtues
}

export const getVirtues = async type => {
  const storageName = `virtues-${type}`
  let virtues = () => JSON.parse(sessionStorage.getItem(storageName))
  if (virtues.length === 0) {
    const res = await getRequest(VIRTUES_URL, { type })
    if (res.data.success) {
      sessionStorage.setItem(storageName, JSON.stringify(res.data.data))
      virtues = res.data.data
    }
  }
  return virtues
}
export const getVirtueById = async (
  virtueId,
  type = VIRTUE_TYPES.WORK_VALUE
) => {
  const virtues = await getVirtues(type)
  return virtues.find(v => v.id === virtueId)
}

export const getUserConnectionLabel = (value, translation) => {
  const labelItem = USER_CONNECTION_RELATIONSHIP.find(
    item => item.value === value
  )
  return translation(`constants.userConnRelationship.${labelItem.value}`)
}

export const getLabelFromOnboardingAnswer = async (questionId, answers) => {
  if (answers[questionId] >= 0) {
    const questions = await getConstant(
      CONSTANTS_OPTIONS.ONBOARDING_QUESTIONNAIRE
    )
    const question = questions.find(q => q.questionId === questionId)
    const option = question?.options.find(
      o => o.value === parseInt(answers[questionId])
    )
    return option?.label
  }
  return null
}

export const getOrgLicenseStatus = organization => {
  let status = null
  if (organization.licenseType && organization.licenseExpireAt) {
    if (moment.utc(organization.licenseExpireAt) > moment.utc()) {
      status = true
    } else {
      status = false
    }
  }
  return status
}

export const userRoleMenuItems = (showSuperAdmin = false) => {
  const roleList = [
    {
      key: USER_ROLE_TYPES.RESTRICTED,
      value: USER_ROLE_TYPES.RESTRICTED,
      label: 'member' // labels are mapped to the translation file
    },
    {
      key: USER_ROLE_TYPES.COACH,
      value: USER_ROLE_TYPES.COACH,
      label: 'coach'
    },
    {
      key: USER_ROLE_TYPES.ADMINISTRATOR,
      value: USER_ROLE_TYPES.ADMINISTRATOR,
      label: 'administrator'
    }
  ]
  if (showSuperAdmin) {
    roleList.push({
      key: USER_ROLE_TYPES.SUPER_ADMIN,
      value: USER_ROLE_TYPES.SUPER_ADMIN,
      label: 'systemAdministrator'
    })
  }
  return roleList
}

export const getUserLicensePaths = (
  licensedGoalIds,
  goals,
  cohortPaths = []
) => {
  if (licensedGoalIds?.length) {
    const allPaths = licensedGoalIds.includes('all')
    const paths = goals.filter(
      p =>
        (licensedGoalIds.includes(p.id) ||
          (allPaths &&
            p.available &&
            !p.leadershipModel.userId &&
            !p.leadershipModel.feature)) &&
        !cohortPaths?.some(c => c.goalId === p.id)
    )
    return paths
  }
  return null
}
export const getUserCohortPaths = (u, cohorts, goals) => {
  return cohorts
    ?.flatMap(c => {
      const user = c.cohortMembers?.some(cm => cm.id === u.id)
      if (user) {
        return [
          ...new Set(
            c.cohortSessions?.map(s => {
              const goal = goals?.find(g => g.id === s.goalId)
              return {
                goalId: goal?.id,
                goalName: `${goal?.leadershipModelName}: ${goal?.name}`,
                startDate: s.startDate
              }
            })
          )
        ]
      }
      return null
    })
    .filter(p => p)
}

export const getCohortGoalFacilitation = async (sessionId, l1Steps) => {
  const res = await getRequest(COHORT_GOAL_FACILITATION_URL, {
    cohortSessionId: sessionId
  })

  const sessionData = res?.data?.data?.sort(
    (a, b) => a.goalTimeline?.number - b.goalTimeline?.number
  )

  sessionData?.map((s, i) => {
    const endStep = l1Steps.find(o => o.id === s.goalTimeline?.endGoalStepId)
    let filteredSteps
    if (i === 0) {
      filteredSteps = l1Steps.filter(o => o.number < endStep.number)
    } else {
      const startStep = l1Steps.find(
        o => o.id === sessionData[i - 1].goalTimeline?.endGoalStepId
      )
      filteredSteps = l1Steps.filter(
        o => o.number < endStep.number && o.number >= startStep.number
      )
    }
    filteredSteps.forEach(f => {
      f.cohortSession = s
    })
    return s
  })
  return l1Steps
}

export const getCurrentL1MilestoneWithCohortSession = async goal => {
  if (!goal?.cohortSession) return

  const { cohortSession } = goal
  let goalStats = goal?.stats
  if (!goalStats) {
    const goalProgress = await getGoalsProgressById(goal.id)
    const steps = await getGoalSteps(goal.id)
    const compiledSteps = compileGoalSteps(
      steps,
      goalProgress?.currentGoalStepId
    )
    goalStats = compiledSteps.stats
  }
  const sessionData = await getCohortGoalFacilitation(
    cohortSession.id,
    goal?.l1Steps
  )
  const currentL1CohortSession = sessionData.find(
    step => step.id === goalStats?.currentL1Milestone?.id
  )?.cohortSession
  if (goalStats.currentL1Milestone) {
    goalStats.currentL1Milestone.cohortSession = currentL1CohortSession
  }
  return { sessionData, currentL1Milestone: goalStats?.currentL1Milestone }
}
